import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { tap } from 'rxjs/operators';
import { MappingRule } from './mapping-rule.model';
import { MappingRulesStore } from './mapping-rules.store';
import { combineLatest } from 'rxjs';
import { AppConfigQuery } from '../../../../core/state/app-config/app-config.query';
import { Cacheable } from 'ts-cacheable';
import { Book } from '../../books/state/book.model';
import { BooksQuery } from '../../books/state/books.query';
import { withTransaction } from '@datorama/akita';

@Injectable({ providedIn: 'root' })
export class MappingRulesService {
    constructor(
        private appConfig: AppConfigQuery,
        private rulesStore: MappingRulesStore,
        private http: HttpClient,
        private booksQuery: BooksQuery
    ) {}

    @Cacheable({ maxCacheCount: 1, maxAge: 1000 * 60 })
    getAll(bookId: Book['id']) {
        this.rulesStore.setLoading(true);
        return combineLatest([this._get(bookId), this._getPending(bookId)]).pipe(
            withTransaction(([rules, pendingTransactions]) => {
                this.rulesStore.set([...rules, ...pendingTransactions]);
                this.rulesStore.setLoading(false);
            })
        );
    }

    private _get(bookId: Book['id']) {
        return this.http.get<MappingRule[]>(
            `${this.appConfig.get().accountingBaseUrl}/accounting/v2/mapping-rules`,
            {
                params: {
                    bookIds: bookId
                }
            }
        );
    }

    private _getPending(bookId: Book['id']) {
        return this.http.get<MappingRule[]>(
            `${
                this.appConfig.get().accountingBaseUrl
            }/accounting/v2/mapping-rules/pending`,
            {
                params: {
                    bookIds: bookId
                }
            }
        );
    }

    add(
        rule: Omit<
            MappingRule,
            | 'matchingTransactionGroupIds'
            | 'updatedAt'
            | 'updatedByUser'
            | 'id'
            | 'affectedTransactionGroupIds'
            | 'status'
        >,
        previousRuleId?: MappingRule['id']
    ) {
        let payload: typeof rule & { previousRuleId?: typeof previousRuleId } = {
            ...rule
        };
        if (previousRuleId) {
            payload = {
                ...payload,
                previousRuleId
            };
        }
        return this.http
            .post<MappingRule>(
                `${this.appConfig.get().accountingBaseUrl}/accounting/v2/mapping-rule`,
                payload
            )
            .pipe(
                withTransaction((addedRule) => {
                    if (previousRuleId) {
                        this.rulesStore.remove(previousRuleId);
                    }
                    this.rulesStore.add(addedRule);
                })
            );
    }

    update(id, rule: Partial<MappingRule>) {
        this.rulesStore.update(id, rule);
    }

    remove(id: MappingRule['id']) {
        return this.http
            .delete<MappingRule>(
                `${
                    this.appConfig.get().accountingBaseUrl
                }/accounting/v2/mapping-rule/${id}`
            )
            .pipe(
                tap((addedRule) => {
                    this.rulesStore.remove(id);
                })
            );
    }
}
